import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { DEFAULT_EVENT_TIMEZONE, formatSessionDateTimeWithZone } from '../../utils/sessionTime';
import { formatEventLocationDisplay } from '../../utils/eventLocation';

const { FiClock, FiVideo, FiCheck } = FiIcons;

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

function getSessionStatus(session) {
  const now = Date.now();
  const start = session.start_time ? new Date(session.start_time).getTime() : 0;
  const end = session.end_time ? new Date(session.end_time).getTime() : start + 60 * 60 * 1000;
  if (end && now > end) return 'completed';
  if (start && now >= start) return 'live';
  if (start && now >= start - FIFTEEN_MINUTES_MS) return 'soon';
  return 'upcoming';
}

function formatSessionTime(iso, timeZone) {
  if (!iso) return '—';
  return formatSessionDateTimeWithZone(iso, timeZone);
}

/**
 * Lists event sessions with Join button (enabled when live or within 15 min).
 * Uses event.zoom_meeting_link or session.zoom_meeting_link.
 */
export default function SessionsList({
  sessions = [],
  eventZoomLink = null,
  onJoinSession,
  eventTimezone = DEFAULT_EVENT_TIMEZONE
}) {
  if (!sessions.length) {
    return (
      <div className="text-slate-500 text-sm py-4">
        No sessions scheduled.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-slate-200">
      {sessions.map((session) => {
        const status = getSessionStatus(session);
        const zoomUrl =
          session.zoom_meeting_link ||
          session.meeting_url ||
          eventZoomLink;
        const canJoin = (status === 'live' || status === 'soon') && zoomUrl;
        const sessionLocationText = formatEventLocationDisplay(session);

        return (
          <li key={session.id} className="py-4 first:pt-0">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">
                  {session.title || session.session_title || `Session ${session.session_number ?? ''}`}
                </p>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <SafeIcon icon={FiClock} className="w-4 h-4 flex-shrink-0" />
                  {formatSessionTime(session.start_time, eventTimezone)}
                </p>
                {sessionLocationText && (
                  <p className="text-sm text-slate-500 mt-1">{sessionLocationText}</p>
                )}
                {status === 'completed' && (
                  <span className="inline-flex items-center gap-1 mt-2 text-sm text-emerald-600">
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    Completed
                  </span>
                )}
                {status === 'live' && (
                  <span className="inline-flex items-center gap-1 mt-2 text-sm text-red-600 font-medium">
                    Live now
                  </span>
                )}
                {status === 'soon' && (
                  <span className="inline-flex items-center gap-1 mt-2 text-sm text-amber-600">
                    Available soon
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canJoin && (
                  <a
                    href={zoomUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00AAFF] text-white font-medium hover:opacity-90 transition-opacity"
                  >
                    <SafeIcon icon={FiVideo} className="w-4 h-4" />
                    Join Session
                  </a>
                )}
                {!canJoin && status === 'upcoming' && zoomUrl && (
                  <span className="text-sm text-slate-400">
                    Available 15 min before start
                  </span>
                )}
                {!canJoin && status === 'upcoming' && !zoomUrl && (
                  <span className="text-sm text-slate-400">Link not yet available</span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
